import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listReports, listOrganizations, deleteReport, exportReport, fmt } from "../lib/api";
import { Plus, FileBarChart2, Download, Pencil, Trash2, Calendar, Building2, ChevronLeft, LineChart } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrgId = searchParams.get("org") || "";

  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  useEffect(() => {
    listOrganizations()
      .then(setOrgs)
      .finally(() => setOrgsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedOrgId) {
      setReports([]);
      return;
    }
    setReportsLoading(true);
    listReports(selectedOrgId)
      .then(setReports)
      .finally(() => setReportsLoading(false));
  }, [selectedOrgId]);

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);

  const selectOrg = (id) => setSearchParams(id ? { org: id } : {});
  const clearOrg = () => setSearchParams({});

  const onDelete = async (id) => {
    if (!window.confirm("Raporu silmek istediğinizden emin misiniz?")) return;
    await deleteReport(id);
    toast.success("Rapor silindi");
    listReports(selectedOrgId).then(setReports);
  };

  const onExport = async (id, name, year) => {
    try {
      toast.info("Word raporu hazırlanıyor…");
      const res = await exportReport(id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "rapor").replace(/\s+/g, "_")}_Karbon_Raporu_${year}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("İndirildi");
    } catch (e) { toast.error("Rapor indirilemedi"); }
  };

  // ---------- ADIM 1: Tesis Seçimi ----------
  if (!selectedOrgId) {
    return (
      <div className="px-6 md:px-14 py-10 md:py-14 max-w-6xl" data-testid="reports-org-picker">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Karbon Ayak İzi</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold">Raporlar</h1>
            <p className="text-sm text-muted-foreground mt-2">Raporlarını görmek istediğiniz tesisi seçin.</p>
          </div>
          <Link to="/reports/new" data-testid="new-report-btn">
            <Button size="lg" className="rounded-full h-12 px-7 gap-2">
              <Plus className="size-4" /> Yeni Rapor
            </Button>
          </Link>
        </div>

        {orgsLoading ? (
          <div className="text-muted-foreground">Yükleniyor…</div>
        ) : orgs.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-14 text-center">
            <Building2 className="size-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.3} />
            <div className="font-display text-xl mb-1">Henüz tesis yok</div>
            <div className="text-sm text-muted-foreground mb-5">Önce bir tesis ekleyin, ardından rapor oluşturabilirsiniz.</div>
            <Link to="/organizations/new">
              <Button className="rounded-full gap-2"><Plus className="size-4" /> Tesis Ekle</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {orgs.map((o) => (
              <button
                key={o.id}
                onClick={() => selectOrg(o.id)}
                className="text-left p-5 rounded-2xl border border-border bg-card card-hover flex gap-4 transition hover:border-primary/40"
                data-testid={`pick-org-${o.id}`}
              >
                <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {o.logo_base64 ? (
                    <img src={o.logo_base64} alt="" className="object-contain w-full h-full" />
                  ) : (
                    <Building2 className="size-6 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold truncate">{o.name}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{o.address || "—"}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
                      <FileBarChart2 className="size-3" /> {o.report_count || 0} Rapor
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- ADIM 2: Seçilen Tesisin Raporları ----------
  return (
    <div className="px-6 md:px-14 py-10 md:py-14 max-w-6xl" data-testid="reports-list">
      <button
        onClick={clearOrg}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-6"
        data-testid="reports-change-org"
      >
        <ChevronLeft className="size-4" /> Tesis Seçimine Dön
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
            {selectedOrg?.logo_base64 ? (
              <img src={selectedOrg.logo_base64} alt="" className="object-contain w-full h-full" />
            ) : (
              <Building2 className="size-6 text-primary" />
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Tesis Raporları</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold">{selectedOrg?.name || "Tesis"}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {reports.length >= 1 && (
            <Link to={`/organizations/${selectedOrgId}/comparison`}>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-6 gap-2" data-testid="reports-compare-btn">
                <LineChart className="size-4" /> Karşılaştır
              </Button>
            </Link>
          )}
          <Link to={`/reports/new?org=${selectedOrgId}`} data-testid="new-report-btn">
            <Button size="lg" className="rounded-full h-12 px-7 gap-2">
              <Plus className="size-4" /> Yeni Rapor
            </Button>
          </Link>
        </div>
      </div>

      {reportsLoading ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-14 text-center">
          <FileBarChart2 className="size-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.3} />
          <div className="font-display text-xl mb-1">Bu tesis için rapor yok</div>
          <div className="text-sm text-muted-foreground mb-5">İlk karbon ayak izi raporunuzu oluşturun.</div>
          <Link to={`/reports/new?org=${selectedOrgId}`}>
            <Button className="rounded-full gap-2"><Plus className="size-4" /> Rapor Oluştur</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-row-${r.id}`}
                 className="p-5 rounded-2xl border border-border bg-card card-hover flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-display text-lg font-semibold">{r.organization_name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                  <Calendar className="size-3" /> {r.year} · {r.frequency || "12 aylık"}
                </div>
              </div>
              <div className="text-right min-w-[120px]">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Toplam</div>
                <div className="font-display text-2xl font-semibold text-primary leading-none">
                  {fmt(r.results?.ozet?.total || 0, 2)}
                </div>
                <div className="text-[11px] text-muted-foreground">ton CO₂e</div>
              </div>
              <div className="flex gap-2">
                <Link to={`/reports/${r.id}`}>
                  <Button variant="outline" size="sm" className="gap-1.5 h-9" data-testid={`report-edit-${r.id}`}>
                    <Pencil className="size-3.5" /> Düzenle
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => onExport(r.id, r.organization_name, r.year)}
                        className="gap-1.5 h-9" data-testid={`report-export-${r.id}`}>
                  <Download className="size-3.5" /> Word
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(r.id)}
                        className="gap-1.5 h-9 text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`report-delete-${r.id}`}>
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
